import { AnnotationPlane } from '~/domain/annotation/annotation';
import {
    CircleRegion,
    CircleRegionDraft,
    circleRegionInFrame,
    createCircleRegionId,
    MIN_CIRCLE_RADIUS_MM,
} from '~/domain/intensity/circleRegion';
import {
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import { circleIntensityStatistics } from '~/engine/data/image/circleIntensityStatistics';
import type { ShownPaneImage } from '~/engine/view/paneImage';
import {
    paneToImageFrame,
    pinToViewPlane,
    viewPlaneCoordinateMm,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import type { Image3D } from '~/domain/patient/image3d';
import { localization } from '~/i18n';
import { AnnotationPlacementTool } from './AnnotationPlacementTool';

/**
 * Shown when a circle is pressed on a tile whose image is not whole, with
 * the counts of that image: figures read from the slices present would
 * look like the region's own (SR-WOR-H-37-3). The words are the reason a
 * disabled tool gives for the same image state.
 */
function circleImageNotWholeMessage(image: Image3D): string {
    const total = image.expectedSliceCount;
    return image.isIncompleteFinal
        ? localization.t('tool.disabled.imageIncomplete', {
              unavailable: image.unavailableSliceCount,
              total,
          })
        : localization.t('tool.disabled.imageLoading', {
              loaded: image.loadedSliceCount,
              total,
          });
}

/** The placement in the pane's frame, with the pane it was started on. */
interface ActiveDraft {
    readonly pane: ShownPaneImage;
    readonly plane: AnnotationPlane;
    readonly centerMm: PointMm;
    radiusMm: number;
}

/**
 * Places a circle. The first click sets the centre, the circle then follows
 * the cursor, and the second click commits it and returns the tool to idle,
 * so the next click starts another circle. The click lifecycle is in
 * {@link AnnotationPlacementTool}.
 *
 * On commit the voxels inside the circle are read once, from the image the
 * pane is showing, whose grid is in the pane's frame. The circle is then
 * stored in that image's own frame. Between the clicks only the radius
 * changes.
 */
export class CircleRegionTool extends AnnotationPlacementTool {
    public readonly toolType = 'circleRegion';

    private readonly _onDraftChanged: (draft: CircleRegionDraft | null) => void;
    private readonly _onCommit: (circle: CircleRegion) => void;

    private _draft: ActiveDraft | null = null;

    public constructor(
        onDraftChanged: (draft: CircleRegionDraft | null) => void,
        onCommit: (circle: CircleRegion) => void
    ) {
        super();
        this._onDraftChanged = onDraftChanged;
        this._onCommit = onCommit;
    }

    /**
     * A tile whose image is not whole is refused before any drafting, with
     * that image's own progress: the panel's gate follows the sidebar image,
     * which need not be the one the pressed tile shows.
     */
    protected override placementRefusal(
        _view: PlaneView,
        pane: ShownPaneImage
    ): string | null {
        const image = pane.image.parent;
        return image.isComplete ? null : circleImageNotWholeMessage(image);
    }

    /**
     * An off-image centre is accepted, unlike a line profile's endpoint: a
     * circle over the image edge still measures the voxels that exist, and one
     * over nothing reports "no data".
     */
    protected override startPlacement(
        _view: PlaneView,
        plane: AnnotationPlane,
        pointMm: PointMm,
        pane: ShownPaneImage
    ): boolean {
        this._draft = { pane, plane, centerMm: pointMm, radiusMm: 0 };
        this.emitDraft();
        return true;
    }

    protected override placePoint(view: PlaneView, pointMm: PointMm): void {
        const draft = this._draft;
        if (!draft) {
            return;
        }
        // The base has checked that the pane still shows the image of the
        // first click. The statistics below also need the view on the
        // circle's plane and that image whole: abandon rather than read
        // values that are not the circle's own.
        const { pane } = draft;
        if (
            !viewShowsPlane(view, draft.plane) ||
            !pane.image.parent.isComplete
        ) {
            this.cancelDraftForChangedImage();
            return;
        }
        const radiusMm = this.radiusTo(view, draft.centerMm, pointMm);
        // A second click on the centre is a mis-click, not a request for a
        // zero-area circle: ignore it and keep rubber-banding.
        if (radiusMm < MIN_CIRCLE_RADIUS_MM) {
            return;
        }
        this._draft = null;
        this.finishPlacement();
        this._onDraftChanged(null);
        this._onCommit(
            circleRegionInFrame(
                {
                    id: createCircleRegionId(),
                    plane: draft.plane,
                    centerMm: draft.centerMm,
                    radiusMm,
                    visible: true,
                    stats: circleIntensityStatistics(
                        pane.image,
                        draft.centerMm,
                        radiusMm,
                        draft.plane.viewDirection
                    ),
                },
                paneToImageFrame(pane)
            )
        );
    }

    protected override moveCursor(view: PlaneView, pointMm: PointMm): void {
        if (!this._draft) {
            return;
        }
        this._draft.radiusMm = this.radiusTo(
            view,
            this._draft.centerMm,
            pointMm
        );
        this.emitDraft();
    }

    protected override undoPoint(): void {
        this.cancelDraft();
    }

    protected override lastPlacedPoint(): PointMm | null {
        return this._draft?.centerMm ?? null;
    }

    protected override discardDraft(): void {
        this._draft = null;
        this._onDraftChanged(null);
    }

    /**
     * The radius: the distance from the centre to the cursor, measured in the
     * plane through the centre. The cursor point is moved onto that plane
     * first, so scrolling to another slice between the two clicks cannot make
     * the radius larger.
     */
    private radiusTo(
        view: PlaneView,
        centerMm: PointMm,
        pointMm: PointMm
    ): number {
        const inPlane = pinToViewPlane(
            pointMm,
            view,
            viewPlaneCoordinateMm(centerMm, view)
        );
        return euclideanDistanceMm(centerMm, inPlane);
    }

    private emitDraft(): void {
        if (!this._draft) {
            return;
        }
        this._onDraftChanged(
            circleRegionInFrame(
                {
                    plane: this._draft.plane,
                    centerMm: this._draft.centerMm,
                    radiusMm: this._draft.radiusMm,
                },
                paneToImageFrame(this._draft.pane)
            )
        );
    }
}
